import { Stack, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Alert from '@components/Alert';
import OrgFeatureToggleList from '@components/OrgFeatureToggleList';
import { getEligibleFeatures } from '@components/PatientPlans';
import RoundedButton from '@components/RoundedButton';
import SkeletonLoader from '@components/SkeletonLoader';

import type { OrgFeatureModel } from 'generated/types';

import type { LicensePlanValues } from '../types';

const messages = defineMessages({
  title: {
    id: 'orgSettings.licensePlan.orgFeatures',
    defaultMessage: 'Organization Features',
  },
  summary: {
    id: 'orgSettings.licensePlan.featuresSummary',
    defaultMessage:
      '{enabled} of {total} features enabled · {available} patient features available to plans',
  },
  catalogError: {
    id: 'newAccount.featureCatalogError',
    defaultMessage: 'Could not load the feature list',
  },
  catalogErrorBody: {
    id: 'orgSettings.licensePlan.featureListError.body',
    defaultMessage:
      'Organization features cannot be changed until this loads. No changes have been saved.',
  },
  retry: {
    id: 'GLOBAL.retry',
    defaultMessage: 'Retry',
  },
});

interface OrgFeaturesTabProps {
  features: OrgFeatureModel[];
  loading?: boolean;
  /** Set when the feature list failed to load — see `listFailed` below. */
  error?: boolean;
  onRetry?: () => void;
}

/**
 * The org's feature entitlements. Presentational: the selection lives in the
 * page's form as `org_feature_ids`, so it survives tab switches and the Patient
 * Plans tab can offer features enabled but not yet saved.
 */
export default function OrgFeaturesTab({
  features,
  loading,
  error,
  onRetry,
}: OrgFeaturesTabProps) {
  const { formatMessage } = useIntl();
  const { isSubmitting, setFieldValue, values } =
    useFormikContext<LicensePlanValues>();

  // An empty list is indistinguishable from "this org has no features", so a
  // failed fetch must not be committable as an empty selection.
  const listFailed = Boolean(error);

  const enabledIds = values.org_feature_ids;
  const availableCount = getEligibleFeatures(features, enabledIds).length;

  /** Add or remove a feature from the org's entitlements. */
  function handleToggleFeature(featureId: string, enabled: boolean) {
    setFieldValue(
      'org_feature_ids',
      enabled
        ? [...enabledIds, featureId]
        : enabledIds.filter((id) => id !== featureId)
    );
  }

  return (
    <SkeletonLoader loading={Boolean(loading)}>
      <Stack gap={2.25}>
        <Stack
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          direction={{ xs: 'column', sm: 'row' }}
          gap={1}
          justifyContent="space-between"
        >
          <Typography variant="h6">{formatMessage(messages.title)}</Typography>
          <Typography
            color="text.secondary"
            data-testid="org-features-summary"
            variant="body2"
          >
            {formatMessage(messages.summary, {
              available: availableCount,
              enabled: enabledIds.length,
              total: features.length,
            })}
          </Typography>
        </Stack>

        {listFailed && (
          <Alert
            action={
              <RoundedButton
                autoSize
                height={32}
                margin={0}
                onClick={() => onRetry?.()}
                variant="white"
              >
                {formatMessage(messages.retry)}
              </RoundedButton>
            }
            data-testid="feature-list-error"
            title={formatMessage(messages.catalogError)}
          >
            {formatMessage(messages.catalogErrorBody)}
          </Alert>
        )}

        <OrgFeatureToggleList
          disabled={isSubmitting || listFailed}
          enabledIds={enabledIds}
          features={features}
          onToggle={handleToggleFeature}
        />
      </Stack>
    </SkeletonLoader>
  );
}
